import { Box, Typography, Button, MenuItem, useTheme, Menu, CircularProgress } from '@mui/material'
import StudentMaterialList from '../../components/study-material-components/StudentMaterialsList'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import { useStudyMaterialStore } from '../../store/studymaterial.store'
import { useOrganizationStore } from '../../store/organization.store'
import CustomSearchBar from '../../components/common/CustomSearchBar'
import { useSearchParams } from 'react-router-dom'
import { useUserStore } from '../../store/user.store'
import { useEffect, useMemo, useState } from 'react'
import { Icons } from '../../utils/utilities'
import { observer } from 'mobx-react'

const Materials = observer( () => {

    const [materials, setMaterials] = useState( [] )
    const [isLoading, setIsLoading] = useState( true )
    const [rowsPerPage, setRowsPerPage] = useState( +( localStorage.getItem( 'StudyMaterialRowsPerPage' ) || 50 ) )
    const [page, setPage] = useState( 0 )
    const [isInstitutesLoading, setIsInstitutesLoading] = useState( false )
    const [instituteMenuAnchor, setInstituteMenuAnchor] = useState( null )
    const [deptMenuAnchor, setDeptMenuAnchor] = useState( null )
    const [departments, setDepartments] = useState( [] )

    const [searchParams, setSearchParams] = useSearchParams( { mode: "manage", searchText: "", institute: "0", department: "0" } )
    const mode = searchParams.get( 'mode' )
    const institute = searchParams.get( 'institute' ) || "0"
    const department = searchParams.get( 'department' ) || "0"
    const searchText = searchParams.get( 'searchText' ).toLowerCase() || ""

    const UserStore = useUserStore()
    const OrganizationStore = useOrganizationStore()

    const { palette, layout } = useTheme()

    const StudyMaterialStore = useStudyMaterialStore()

    const searchMaterials = ( e ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "searchText", e.target.value )
            return newParams
        }, { replace: true } )
        localStorage.setItem( "materialsSearchText", e.target.value )
    }

    const handleStudentInstituteChange = ( val ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "institute", val )
            newParams.set( "department", "0" )
            return newParams
        }, { replace: true } )
        setInstituteMenuAnchor( null )
        const depts = new Set( StudyMaterialStore.getMaterials["materials"].map( sm => sm.department_name ) )

        setDepartments( Array.from( depts ).filter( dept => Boolean( dept ) ).sort() )
        localStorage.setItem( "materialsSearchInstitute", val )
    }

    const handleStudentDeptChange = ( val ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "department", val )
            return newParams
        }, { replace: true } )
        setDeptMenuAnchor( null )
    }

    const handleChangePage = ( event, newPage ) => {
        setPage( newPage )
    }

    const removeFilters = async () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "institute", "0" )
            newParams.set( "department", "0" )
            newParams.set( "searchText", "" )
            return newParams
        }, { replace: true } )
        if ( StudyMaterialStore.getErrors.materials )
            await StudyMaterialStore.fetchMaterials()
        setMaterials( StudyMaterialStore.getMaterials["materials"] )
    }

    const handleChangeRowsPerPage = ( event ) => {
        setRowsPerPage( parseInt( event.target.value, 10 ) )
        localStorage.setItem( 'StudyMaterialRowsPerPage', event.target.value )
        setPage( 0 )
    }

    useEffect( () => {
        const getData = async () => {
            localStorage.setItem( "studyMaterialMode", "manage" )
            setIsInstitutesLoading( true )
            if ( OrganizationStore.getOrganization?.length === 0 )
                await OrganizationStore.fetchOrganization()
            setIsInstitutesLoading( false )
            if ( StudyMaterialStore.getErrors.materials )
                await StudyMaterialStore.fetchMaterials()
            setMaterials( StudyMaterialStore.getMaterials["materials"] )
            setIsLoading( false )
        }
        getData()
    }, [UserStore, StudyMaterialStore, OrganizationStore] )

    const filteredMaterials = useMemo( () => {
        return materials.filter( material => {
            const { subject_name, study_material_name, subject_code, study_material_created_by, study_material_tags, institute: matInstitute, department_name } = material

            // Lowercase in advance
            const searchableJoined = `
                    ${subject_name ?? ""}
                    ${subject_code ?? ""}
                    ${study_material_created_by ?? ""}
                    ${study_material_name ?? ""}
                    ${study_material_tags ?? ""}
            `.toLowerCase()

            return ( searchableJoined.includes( searchText ) ) &&
                ( matInstitute === institute || institute === "0" ) &&
                ( department === department_name || department === "0" )
        } )
    }, [materials, searchText, institute, department] )

    return (
        <Box minWidth="0" overflow="auto" gap="10px" flexGrow={1} display="flex" flexDirection="column" padding={layout.pagePadding}>

            {/* Main Content */}
            <Box >
                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Your Study Materials</Typography>
                <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Manage your material from here.</Typography>
            </Box>
            {!isLoading && <Box display="flex" flexWrap="wrap" gap="10px" alignItems="stretch">
                {UserStore.getUser["user_role"] === "STUDENT" &&
                    <Box display="flex" gap={layout.cardsGap}>
                        <Box flexShrink={0} display="flex">
                            <Button disabled={isInstitutesLoading} sx={{ height: "100%", flexShrink: 0, justifyContent: "space-between" }} variant='contained' disableElevation endIcon={isInstitutesLoading ? <CircularProgress size={14} /> : Icons.small.DropDown} onClick={e => setInstituteMenuAnchor( e.currentTarget )} color="customThemeColor">{isInstitutesLoading ? "Fetching institutes..." : institute !== "0" ? institute : "Select institute"}</Button>
                            <Menu sx={{ marginTop: "5px" }} onClose={() => setInstituteMenuAnchor( null )} anchorEl={instituteMenuAnchor} open={Boolean( instituteMenuAnchor )} >
                                <MenuItem onClick={() => handleStudentInstituteChange( "0" )} dense key={"all"} sx={{ fontSize: "14px", textTransform: "capitalize" }} value={"0"}>
                                    All institutes
                                </MenuItem>
                                {OrganizationStore.getOrganization.map( org => {
                                    return <MenuItem onClick={() => handleStudentInstituteChange( org.institute_name_short )} dense key={org.institute_id} sx={{ fontSize: "14px", textTransform: "capitalize" }} value={org.institute_name_short}>
                                        {org.institute_name.toLowerCase()}
                                    </MenuItem>
                                } )}
                            </Menu>
                        </Box>
                        <Box flexShrink={0} display="flex">
                            <Button disabled={isInstitutesLoading || institute === "0"} sx={{ height: "100%", flexShrink: 0, justifyContent: "space-between" }} variant='contained' disableElevation endIcon={isInstitutesLoading ? <CircularProgress size={14} /> : Icons.small.DropDown} onClick={e => setDeptMenuAnchor( e.currentTarget )} color="customThemeColor">{department !== "0" ? department : "All Department"}</Button>
                            <Menu sx={{ marginTop: "5px" }} onClose={() => setDeptMenuAnchor( null )} anchorEl={deptMenuAnchor} open={Boolean( deptMenuAnchor )} >
                                <MenuItem onClick={() => handleStudentDeptChange( "0" )} dense key={"all"} sx={{ fontSize: "14px", textTransform: "capitalize" }} value={"0"}>
                                    All Departments
                                </MenuItem>
                                {departments.map( dept => {
                                    return <MenuItem onClick={() => handleStudentDeptChange( dept )} dense key={dept} sx={{ fontSize: "14px", textTransform: "capitalize" }} value={dept}>
                                        {dept?.toLowerCase()}
                                    </MenuItem>
                                } )}
                            </Menu>
                        </Box>
                    </Box>
                }
                <CustomSearchBar style={{ flexGrow: 1, width: "400px", marginRight: "0" }} value={searchText} onChange={searchMaterials} placeholder={`Search by Subjects,${UserStore.getUser.user_role === 'STUDENT' ? " Faculty," : ""} Title or Subject code...`} />
            </Box>}


            {!isLoading
                && filteredMaterials
                && filteredMaterials.length > 0
                && UserStore.getUser.user_role === 'STUDENT'
                && <StudentMaterialList filteredMaterials={filteredMaterials} page={page} rowsPerPage={rowsPerPage} handleChangePage={handleChangePage} handleChangeRowsPerPage={handleChangeRowsPerPage} />}


            {!isLoading && <Box flexGrow={1} display="flex" flexDirection="column">
                {( !filteredMaterials || filteredMaterials.length === 0 ) && <Box display="flex" flexDirection="column" flexGrow={1}>
                    {mode === "manage" && <Box margin="10px" borderRadius="10px" padding="20px" flexGrow={1} bgcolor={palette.contentBg} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                        < Typography textAlign="center" color="textSecondary" variant='subtitle2'>{( institute === "0" && searchText === "" ) ? "Please select an institute to browse study materials or search" : "Selected institute doesn't seem to have any study materials"}</Typography>
                        <Box marginTop="20px" display="flex" gap="20px" alignItems="center" flexWrap="wrap">
                            {materials && ( materials.length > 0 || searchText !== "" || institute !== "0" ) && <Button onClick={removeFilters} disableElevation variant='contained' color="secondary" sx={{ textTransform: "capitalize" }}>Remove filter</Button>}
                        </Box>
                    </Box>}
                </Box>}

            </Box>}
            {isLoading && <DataLoadingSpinner padding="10px 0" waitingMessage="Fetching materials..." />}
        </Box >
    )
} )

export default Materials
